import { useCallback, useEffect, useMemo } from 'react';

/**
 * Presentor popup management (refactor Step 6). Opens the Presentor in a
 * named, screen-aware popup, tracks opener windows by recipient_id (with a
 * 'last' fallback), and listens for the `presentor:completed` postMessage to
 * close the popup, refresh the deployment's users, surface persistence
 * failures, and stash a quick-view results payload in sessionStorage.
 * All logic moved verbatim from App.js.
 *
 * Params:
 *  - selectedDeploymentId: current selection (scopes the results stash and
 *    gates the deployment-users refresh).
 *  - refreshDeploymentUsers: () => Promise — UsersPanel reload bridge.
 *  - onPersistenceFailure: ({ sessionId, reason, ts }) => void — ops banner
 *    surface (currently unrendered; kept for the expanded SRA UI).
 *
 * Returns { openPresentor }.
 */
export default function usePresentorWindows({ selectedDeploymentId, refreshDeploymentUsers, onPersistenceFailure }) {
  // Track opener windows for Presentor by recipient_id (or 'last')
  const presentorWindows = useMemo(() => new Map(), []);

  const openPresentor = useCallback((url) => {
    try {
      // Screen-aware popup sizing: wider when desktop allows for media panel
      const sw = window.screen.availWidth || 800;
      const sh = window.screen.availHeight || 700;
      const popW = sw >= 1100 ? 1040 : 800;
      const popH = sw >= 1100 ? 720 : 700;
      const left = Math.max(0, Math.round((sw - popW) / 2));
      const top = Math.max(0, Math.round((sh - popH) / 2));
      const features = `width=${popW},height=${popH},left=${left},top=${top},scrollbars=yes,resizable=yes`;
      const w = window.open(url, 'SamSurveyPresentor', features);
      if (!w) return;
      // Try to map by recipient id; also keep a 'last' key fallback
      try {
        const u = new URL(url);
        const rid = u.searchParams.get('r');
        if (rid) presentorWindows.set(rid, w);
      } catch (_) { }
      presentorWindows.set('last', w);
    } catch (_) { }
  }, [presentorWindows]);

  // Listen for presentor:completed and close opened window, then refresh and show inline results
  useEffect(() => {
    const onMsg = async (event) => {
      const data = event && event.data;
      if (!data || data.type !== 'presentor:completed') return;
      try {
        const rid = data.recipient_id;
        let win = null;
        if (rid && presentorWindows.has(rid)) {
          win = presentorWindows.get(rid);
          presentorWindows.delete(rid);
        } else if (presentorWindows.has('last')) {
          win = presentorWindows.get('last');
          presentorWindows.delete('last');
        }
        try { win && win.close && win.close(); } catch (_) { }
      } catch (_) { }
      // Refresh UsersPanel's deployment users to reflect completion
      try {
        if (selectedDeploymentId) {
          await refreshDeploymentUsers();
        }
      } catch (_) { }
      // Capture persistence status for ops banner
      try {
        const rp = Object.prototype.hasOwnProperty.call(data, 'resultsPersisted') ? data.resultsPersisted : undefined;
        const pr = Object.prototype.hasOwnProperty.call(data, 'persistReason') ? data.persistReason : undefined;
        if (rp === false && pr) {
          onPersistenceFailure({ sessionId: data.sessionId || null, reason: String(pr), ts: Date.now() });
        }
      } catch (_) { }
      // If results payload provided, stash for quick view in session
      try {
        if (data && Array.isArray(data.results)) {
          const key = `console_last_results_${selectedDeploymentId || 'global'}`;
          sessionStorage.setItem(key, JSON.stringify({ ts: Date.now(), results: data.results }));
        }
      } catch (_) { }
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presentorWindows, selectedDeploymentId]);

  return { openPresentor };
}
